import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import cmsApi from "@/api/cms";
import type { NavigationDto } from "@/types";
import { cn } from "@/lib/utils";
import Logo from "../ui/Logo";
import { defaultNavItems } from "@/repository/navigation";
import { useLanguage } from "@/context/LanguageContext";

const FEATURE_PROMO_IMAGE = "/feature-link.svg";
const ARROW_ICON = "/feature-arrow.svg";

const Navigation: React.FC<{ isLightHeader: boolean }> = ({
  isLightHeader,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data: navItems = defaultNavItems } = useQuery({
    queryKey: ["navigation", "primary-nav", language],
    queryFn: () => cmsApi.getNavigation("primary-nav", language),
  });

  const isMenuOpenOrLight = isLightHeader || isMobileMenuOpen;
  const headerBgClass = isMenuOpenOrLight
    ? "bg-white"
    : "bg-primary text-white";

  return (
    <nav
      className={cn("relative transition-all duration-300 z-50", headerBgClass)}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-[88px]">
          <Link
            to="/"
            className="relative z-50 flex items-center h-full focus:outline-none"
          >
            <Logo isDark={isMenuOpenOrLight} />
          </Link>

          <div className="hidden lg:flex items-center h-full">
            {navItems.map((item) => (
              <NavItem key={item.id} item={item} isLight={isLightHeader} />
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              className={cn(
                "hidden lg:inline-block text-sm font-semibold py-3 px-6 rounded-lg transition-colors",
                isLightHeader
                  ? "bg-primary text-white hover:bg-primary/90"
                  : "bg-white text-basic hover:bg-gray-100",
              )}
            >
              Get a demo
            </Link>

            <HamburgerButton
              isOpen={isMobileMenuOpen}
              toggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              isDarkColor={isMenuOpenOrLight}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && <MobileMenu navItems={navItems} />}
      </AnimatePresence>
    </nav>
  );
};

// ----------------------------------------------------------------------
// Mobile Components
// ----------------------------------------------------------------------

const HamburgerButton = ({
  isOpen,
  toggle,
  isDarkColor,
}: {
  isOpen: boolean;
  toggle: () => void;
  isDarkColor: boolean;
}) => {
  const colorClass = isDarkColor ? "bg-slate-900" : "bg-white";

  const topVariants = {
    closed: { rotate: 0, translateY: 0 },
    open: { rotate: 45, translateY: 8 },
  };

  const centerVariants = {
    closed: { opacity: 1 },
    open: { opacity: 0 },
  };

  const bottomVariants = {
    closed: { rotate: 0, translateY: 0 },
    open: { rotate: -45, translateY: -8 },
  };

  return (
    <button
      onClick={toggle}
      className="lg:hidden relative z-50 p-2 w-10 h-10 flex flex-col justify-center items-center gap-1.5 focus:outline-none"
      aria-label="Toggle menu"
    >
      <motion.div
        variants={topVariants}
        animate={isOpen ? "open" : "closed"}
        className={cn(
          "w-6 h-0.5 rounded-full origin-center transition-colors",
          colorClass,
        )}
      />
      <motion.div
        variants={centerVariants}
        animate={isOpen ? "open" : "closed"}
        className={cn("w-6 h-0.5 rounded-full transition-colors", colorClass)}
      />
      <motion.div
        variants={bottomVariants}
        animate={isOpen ? "open" : "closed"}
        className={cn(
          "w-6 h-0.5 rounded-full origin-center transition-colors",
          colorClass,
        )}
      />
    </button>
  );
};

const MobileMenu = ({ navItems }: { navItems: NavigationDto[] }) => {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "100vh" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="absolute top-full left-0 w-full bg-white border-t border-gray-100 overflow-y-auto pb-24 shadow-xl lg:hidden"
      style={{ height: "calc(100vh - 88px)" }}
    >
      <div className="container mx-auto px-6 lg:px-12 py-6 flex flex-col gap-2">
        {navItems.map((item) => (
          <MobileNavItem key={item.id} item={item} />
        ))}

        <hr className="border-gray-100 my-4" />

        <Link
          to="/contact"
          className="w-full text-center text-sm font-semibold py-3 px-6 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        >
          Get a demo
        </Link>
      </div>
    </motion.div>
  );
};

const MobileNavItem = ({ item }: { item: NavigationDto }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  if (!hasChildren) {
    return (
      <Link
        target={item.target}
        to={item.url}
        className="block py-4 text-lg font-medium text-slate-800 border-b border-gray-50 last:border-0"
      >
        {item.title}
      </Link>
    );
  }

  return (
    <div className="border-b border-gray-50 last:border-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full py-4 text-lg font-medium text-slate-800"
      >
        <span>{item.title}</span>
        <motion.svg
          animate={{ rotate: isExpanded ? 180 : 0 }}
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-4 pl-2 space-y-4">
              {item.children?.map((child) => (
                <Link
                  key={child.id}
                  to={child.url}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50"
                >
                  {child.iconUrl && (
                    <img
                      src={child.iconUrl}
                      alt=""
                      className="w-5 h-5 mt-1 object-contain opacity-70"
                    />
                  )}
                  <div>
                    <span className="block text-base font-medium text-slate-700">
                      {child.title}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Automate your {child.title.toLowerCase()}
                    </span>
                  </div>
                </Link>
              ))}
              <Link
                to="/features"
                className="block mt-4 p-6 bg-slate-900 rounded-xl text-white"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">See all features</span>
                  <img src={ARROW_ICON} alt="" className="w-6 h-6" />
                </div>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ----------------------------------------------------------------------
// Desktop Components (Unchanged)
// ----------------------------------------------------------------------

const NavItem: React.FC<{ item: NavigationDto; isLight: boolean }> = ({
  item,
  isLight,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;

  const isActive = location.pathname.startsWith(item.url) && item.url !== "#";
  const textColor = isLight
    ? "text-slate-800 hover:text-primary"
    : "text-white hover:text-white/80";

  return (
    <div
      className="h-full flex items-center static"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={item.url}
        target={item.target}
        className={cn(
          "relative z-20 px-4 py-9 flex items-center text-sm font-medium transition-colors rounded-md",
          textColor,
          isActive && "font-semibold",
        )}
      >
        {item.title}
        {hasChildren && (
          <svg
            className={cn(
              "w-4 h-4 ml-1 transition-transform duration-200",
              isHovered ? "rotate-180" : "",
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </Link>

      <AnimatePresence>
        {hasChildren && isHovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-[#F4F8FF] border-t border-gray-100 shadow-xl overflow-hidden z-10"
          >
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 30 }}
              className="container mx-auto px-6 lg:px-12 py-14.5"
            >
              <div className="flex gap-30">
                <div className="flex-1 grid grid-flow-col grid-rows-3 grid-cols-2 gap-x-15 gap-y-8">
                  {item.children?.map((child) => (
                    <div onClick={() => setIsHovered(false)} key={child.id}>
                      <MegaMenuLink item={child} />
                    </div>
                  ))}
                </div>

                <div onClick={() => setIsHovered(false)}>
                  <Link
                    to="/features"
                    className="inline-flex items-center text-sm font-semibold decoration-2 underline-offset-4"
                  >
                    <div className="w-[321px] h-[315px] shrink-0 relative group rounded-2xl overflow-hidden bg-slate-900 shadow-md">
                      <img
                        src={FEATURE_PROMO_IMAGE}
                        alt="Feature highlight"
                        className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="relative z-10 p-4.5 h-full flex justify-between items-end gap-11 text-white">
                        <h3 className="font-medium text-lg/6 ">
                          Find more about our features
                        </h3>
                        <img
                          src={ARROW_ICON}
                          alt="Arrow right"
                          className="w-14 h-14"
                        />
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MegaMenuLink = ({ item }: { item: NavigationDto }) => (
  <Link
    to={item.url}
    className={cn(
      "group flex gap-4 rounded-xl hover:bg-slate-50 transition-colors",
      item.description ? "items-start " : "items-center",
    )}
    target={item.target}
  >
    <div className="shrink-0 mt-1">
      {item.iconUrl ? (
        <img
          src={item.iconUrl}
          alt=""
          className="w-8 h-8 object-contain drop-shadow-sm"
        />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <div className="w-5 h-5 bg-current rounded-sm" />
        </div>
      )}
    </div>
    <div>
      <h4 className="text-base/5 font-medium text-basic group-hover:text-primary transition-colors">
        {item.title}
      </h4>
      {item.description && (
        <p className="text-xs/4 mt-2 text-basic">{item.description}</p>
      )}
    </div>
  </Link>
);

export default Navigation;
